import awsExports from '../aws-exports';

/**
 * Sign out of the FITTWorks hub reliably.
 *
 * `Auth.signOut()` (and `Auth.signOut({ global: true })`) has proven unreliable for a hosted-UI
 * session in this app: it can resolve WITHOUT clearing the local Cognito tokens, without calling
 * GlobalSignOut, and without redirecting to the hub /logout endpoint — and it throws no error.
 * Verified in the browser: after clicking "Sign Out", all six `CognitoIdentityServiceProvider.*`
 * tokens were still in localStorage, so Amplify's `authStatus` stayed `authenticated` and the app
 * bounced the user straight back in. That IS the "sign-out doesn't work" report.
 *
 * So do the two things it was supposed to do, explicitly and in order:
 *   1. Purge the local Amplify/Cognito tokens ourselves, so `authStatus` cannot stay authenticated
 *      when we land back on the app.
 *   2. Full-page redirect to the hub /logout endpoint, which clears the hub's OWN session cookie —
 *      without that, LoginRoute's `federatedSignIn` silently re-authenticates against the live cookie
 *      and you never actually leave.
 *
 * Order matters: clearing the tokens before the redirect is what makes the return trip land on the
 * login page instead of the dashboard. The redirect is a full-page navigation, so nothing runs after.
 *
 * ponytail: deliberately does NOT call `Auth.signOut({ global: true })` for server-side refresh-token
 * revocation — that call is exactly the one that hangs/no-ops here, and racing it against this
 * teardown reintroduces the flakiness. This clears the token locally, which is a real logout; if
 * "sign out on all devices" is ever required, add a bounded, non-blocking GlobalSignOut before step 1.
 */
export const hubSignOut = () => {
  for (const key of Object.keys(localStorage)) {
    if (key.includes('CognitoIdentityServiceProvider') || key.toLowerCase().includes('amplify')) {
      localStorage.removeItem(key);
    }
  }
  localStorage.removeItem('activeOrganization');
  localStorage.removeItem('lastVisitedPath');
  sessionStorage.clear();

  const domain = awsExports.oauth && awsExports.oauth.domain;
  const clientId = awsExports.aws_user_pools_web_client_id;
  const logoutUri = `${window.location.origin}/`;

  // If the hub config is somehow missing, still do a local sign-out rather than throw — land on the
  // app origin, where LoginRoute (now token-less) will start a fresh sign-in.
  const target = domain && clientId
    ? `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`
    : logoutUri;

  window.location.assign(target);
};
